import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Workflow as WorkflowIcon, Trash2, MoreHorizontal, Copy, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useWorkspace } from "@/context/workspace-context";
import { useAgent } from "@/hooks/use-agents";
import { workflowsApi, type WorkflowListRow } from "@/api/workflows";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { WorkflowInsightsView } from "@/components/workflows/insights/insights-view";

/**
 * Workflows module — top-nav surface.
 *
 * Agent-aware: when the workspace has an agent selected (the chat thread's
 * agentId), this scopes the list to workflows that reference *that* agent —
 * so navigating to an agent and opening Workflows shows only its workflows,
 * not the whole tenant's. A scope switcher flips between "this agent" and
 * "all workflows". With no agent selected it falls back to the tenant-wide
 * list.
 *
 * Admins get a "+ New Workflow" button and a per-row delete action. Deleting
 * removes the workflow from the platform and tears down its schedule triggers
 * server-side, so the agent stops running it. Clicking a row navigates to the
 * canvas editor at /tenants/:id/workflows/:workflowId.
 */
export function WorkflowsSurface() {
  const { tenantId, isAdmin } = useTenant();
  const { threadContext } = useWorkspace();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const agentId = threadContext.agentId ?? null;
  const sessionId = threadContext.sessionId ?? null;
  const channelId = threadContext.channelId ?? null;
  const { data: selectedAgent } = useAgent(agentId ?? "");

  // Default to the agent's workflows when an agent is in context; let the
  // user widen to the full tenant list with the scope switcher. Falls back
  // to "all" automatically when nothing is selected.
  //
  // Scope lives in the URL, not local state, so opening a workflow and
  // pressing back restores the list the user was actually looking at. Held in
  // local state it was remounted at the default, dropping anyone who had
  // widened to "All workflows" back to "This agent". Same reasoning as `mode`
  // in workspace-context.tsx: the URL is the source of truth for view state.
  const [searchParams, setSearchParams] = useSearchParams();
  const scope: "agent" | "all" = searchParams.get("wfScope") === "all" ? "all" : "agent";
  // All | Insights. Also in the URL (`wfTab`) so the drill-in breadcrumb can
  // bring the user back to the tab they left.
  const tab: "all" | "insights" = searchParams.get("wfTab") === "insights" ? "insights" : "all";
  const setTab = (next: "all" | "insights") => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") {
      params.delete("wfTab");
      params.delete("period");
    } else {
      params.set("wfTab", "insights");
    }
    setSearchParams(params, { replace: true });
  };
  const setScope = (next: "agent" | "all") => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        // Only the non-default needs carrying, so a plain URL stays clean.
        if (next === "all") params.set("wfScope", "all");
        else params.delete("wfScope");
        return params;
      },
      { replace: true },
    );
  };
  const scopedToAgent = !!agentId && scope === "agent";

  const { data: workflows = [], isLoading } = useQuery({
    queryKey: scopedToAgent
      ? queryKeys.workflowsByAgent(agentId!)
      : queryKeys.workflows.byCompany(tenantId),
    queryFn: scopedToAgent
      ? () => workflowsApi.listForAgent(agentId!)
      : () => workflowsApi.list(tenantId),
    enabled: scopedToAgent ? !!agentId : !!tenantId,
  });

  // Search filters the rows already in memory. Both list endpoints return
  // every workflow for the scope with no search parameter, so there is
  // nothing to refetch and no debounce to add.
  //
  // Name only, deliberately. Matching the description as well made a search
  // for "demo" return workflows whose name looked unrelated, because the word
  // sat in their description — the row read as a false positive.
  const [search, setSearch] = useState("");
  const searchTerm = search.trim().toLowerCase();
  const visibleWorkflows = searchTerm
    ? workflows.filter((w) => w.name.toLowerCase().includes(searchTerm))
    : workflows;

  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<WorkflowListRow | null>(null);

  // Both the tenant-wide and agent-scoped lists can show a deleted/created
  // workflow, so refresh both keys after a mutation regardless of scope.
  const invalidateWorkflowLists = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.workflows.byCompany(tenantId) });
    if (agentId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflowsByAgent(agentId) });
    }
  };

  // Keep the full chat identity while visiting a workflow. The global Chat
  // tab reads these URL values, so dropping the session here would return the
  // user to whichever conversation happens to be first in the channel.
  const canvasPath = (workflowId: string) => {
    const params = new URLSearchParams();
    if (agentId) params.set("agentId", agentId);
    if (sessionId) params.set("sessionId", sessionId);
    if (channelId && channelId !== "general") params.set("channelId", channelId);
    // Carry the list scope so the canvas can send the user back to the list
    // they came from. The back arrow and the Workflows tab both build a fresh
    // URL rather than using browser history, so without this they would land
    // on "This agent" no matter which list was open.
    if (scope === "all") params.set("wfScope", "all");
    const qs = params.toString();
    return `/tenants/${tenantId}/workflows/${workflowId}${qs ? `?${qs}` : ""}`;
  };

  const createWorkflow = useMutation({
    // Associate the workflow with the in-context agent so it belongs to that
    // agent from the start (server seeds an Agent node bound to it).
    mutationFn: (name: string) =>
      workflowsApi.create(tenantId, agentId ? { name, agentId } : { name }),
    onSuccess: (workflow) => {
      invalidateWorkflowLists();
      setCreating(false);
      navigate(canvasPath(workflow.id));
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to create workflow:", err);
      alert(`Failed to create workflow: ${message}`);
    },
  });

  const deleteWorkflow = useMutation({
    mutationFn: (workflowId: string) => workflowsApi.delete(workflowId),
    onSuccess: () => {
      invalidateWorkflowLists();
      setConfirmDelete(null);
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to delete workflow:", err);
      alert(`Failed to delete workflow: ${message}`);
    },
  });

  // Duplicate: copy a workflow's full graph into a brand-new workflow (like the
  // New Workflow flow, landing on the canvas), but never with the same name —
  // we append "(copy)" and bump a counter if that is already taken.
  const uniqueCopyName = (base: string) => {
    const existing = new Set(workflows.map((w) => w.name));
    let candidate = `${base} (copy)`;
    let n = 2;
    while (existing.has(candidate)) candidate = `${base} (copy ${n++})`;
    return candidate.slice(0, 200);
  };

  const duplicateWorkflow = useMutation({
    mutationFn: async (row: WorkflowListRow) => {
      const source = await workflowsApi.get(row.id);
      const created = await workflowsApi.create(tenantId, {
        name: uniqueCopyName(row.name),
        description: source.description ?? undefined,
      });
      // create() only seeds a starter node; copy the source graph over it.
      if (source.nodes.length > 0 || source.edges.length > 0) {
        await workflowsApi.update(created.id, {
          nodes: source.nodes,
          edges: source.edges,
        });
      }
      return created;
    },
    onSuccess: (created) => {
      invalidateWorkflowLists();
      navigate(canvasPath(created.id));
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to duplicate workflow:", err);
      alert(`Failed to duplicate workflow: ${message}`);
    },
  });

  const agentName = selectedAgent?.name ?? "this agent";

  // Guided walkthrough hook: open (or create) a demo workflow and land on its
  // canvas, so the "Build a workflow" guide can show the canvas rather than
  // just pointing at the New Workflow button. Reuses an existing demo to avoid
  // piling up duplicates on repeat runs.
  useEffect(() => {
    const onDemo = () => {
      const existing = workflows.find((w) => w.name === "Demo workflow");
      if (existing) navigate(canvasPath(existing.id));
      else if (!createWorkflow.isPending) createWorkflow.mutate("Demo workflow");
    };
    window.addEventListener("proclaw:create-demo-workflow", onDemo);
    return () => window.removeEventListener("proclaw:create-demo-workflow", onDemo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflows, createWorkflow.isPending]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 border-b border-border bg-surface">
        <div className="mx-auto flex min-h-14 w-full max-w-6xl flex-wrap items-center justify-between gap-3 px-6 py-2 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-[15px] font-semibold tracking-tight text-text-primary">
              Workflows
            </h1>
            <div className="flex items-center gap-0.5" role="tablist" aria-label="Workflows view">
              {(["all", "insights"] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  onClick={() => setTab(key)}
                  className={cn(
                    "rounded-md px-[9px] py-1 text-[12px] font-medium transition-colors",
                    tab === key ? "bg-surface-hover text-text-primary" : "text-text-secondary hover:text-text-primary",
                  )}
                >
                  {key === "all" ? "All" : "Insights"}
                </button>
              ))}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            {/* Scope switcher — only meaningful when an agent is in context. */}
            {agentId && (
              <div className="flex items-center rounded-md border border-border bg-surface p-0.5 text-xs">
                <button
                  onClick={() => setScope("agent")}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium transition-colors",
                    scope === "agent"
                      ? "bg-accent/10 text-accent"
                      : "text-text-muted hover:text-text-primary",
                  )}
                >
                  This agent
                </button>
                <button
                  onClick={() => setScope("all")}
                  className={cn(
                    "rounded px-2.5 py-1 font-medium transition-colors",
                    scope === "all"
                      ? "bg-accent/10 text-accent"
                      : "text-text-muted hover:text-text-primary",
                  )}
                >
                  All workflows
                </button>
              </div>
            )}
            {isAdmin && (
              <Button
                size="sm"
                data-tour="workflow-create"
                onClick={() => setCreating(true)}
                className="gap-2"
              >
                <Plus size={14} />
                New Workflow
              </Button>
            )}
          </div>
        </div>
      </div>

      {tab === "insights" ? (
        <WorkflowInsightsView agentId={scopedToAgent ? agentId : null} />
      ) : (
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl p-6 lg:p-8">
          {/* Empty state / list */}
          {isLoading ? (
            <p className="py-12 text-center text-sm text-text-muted">Loading workflows…</p>
          ) : workflows.length === 0 ? (
            scopedToAgent ? (
              <AgentEmptyState agentName={agentName} />
            ) : (
              <EmptyState canCreate={isAdmin} onCreate={() => setCreating(true)} />
            )
          ) : (
            <>
              <div className="relative mb-4">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
                />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search workflows by name..."
                  aria-label="Search workflows by name"
                  className="w-full rounded-md border border-border bg-surface py-2 pl-8 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-border"
                />
              </div>

              {visibleWorkflows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <Search size={32} className="mb-3 text-text-muted" />
                  <p className="text-sm text-text-muted">
                    No workflows match &ldquo;{search.trim()}&rdquo;.
                  </p>
                </div>
              ) : (
              <div className="overflow-hidden rounded-lg border border-border bg-surface">
                {/* table-fixed + explicit widths so the long Name/description
                    column truncates instead of blowing the table past the
                    container's right edge (truncate is a no-op in an
                    auto-layout table cell). */}
                <table className="w-full table-fixed text-sm">
                  <thead>
                    <tr className="border-b border-border text-[11px] font-semibold uppercase tracking-wide text-text-muted">
                      <Th>Name</Th>
                      <Th className="w-24">Status</Th>
                      <Th className="w-20">Agents</Th>
                      <Th className="w-40 max-md:hidden">Last run</Th>
                      <Th className="w-32 max-md:hidden">Created</Th>
                      <Th className="w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleWorkflows.map((w) => (
                      <WorkflowRow
                        key={w.id}
                        workflow={w}
                        isAdmin={isAdmin}
                        onOpen={() => navigate(canvasPath(w.id))}
                        onDelete={() => setConfirmDelete(w)}
                        onDuplicate={() => duplicateWorkflow.mutate(w)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </>
          )}
        </div>
      </div>
      )}

      {creating && (
        <NewWorkflowModal
          onCancel={() => setCreating(false)}
          onConfirm={(name) => createWorkflow.mutate(name)}
          pending={createWorkflow.isPending}
        />
      )}

      {confirmDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-[min(400px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-sm font-semibold text-text-primary">Delete workflow</h3>
            <p className="mb-4 text-xs text-text-secondary">
              Are you sure you want to delete{" "}
              <span className="font-medium text-text-primary">"{confirmDelete.name}"</span>?
              This removes the workflow and any of its schedules, so the
              agent will stop running it. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="destructive"
                disabled={deleteWorkflow.isPending}
                onClick={() => deleteWorkflow.mutate(confirmDelete.id)}
              >
                {deleteWorkflow.isPending ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <th className={cn("px-4 py-2.5 text-left font-semibold", className)}>{children}</th>;
}

function WorkflowRow({
  workflow,
  isAdmin,
  onOpen,
  onDelete,
  onDuplicate,
}: {
  workflow: WorkflowListRow;
  isAdmin: boolean;
  onOpen: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // The menu is rendered in a portal (fixed-positioned) so the table card's
  // overflow-hidden and the list's scroll container can never clip it. We track
  // its screen coordinates, computed from the trigger button, and flip it above
  // the button when there isn't room below in the viewport.
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const MENU_W = 144; // matches w-36
  const MENU_H = 76; // approx height of the two-item menu

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const flipUp = window.innerHeight - rect.bottom < MENU_H + 12;
    setMenuPos({
      top: flipUp ? rect.top - MENU_H - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - MENU_W), // right-align to the button
    });
    setMenuOpen(true);
  };

  // Close on click outside (button + popup), on Escape, and on scroll/resize
  // (a fixed menu would otherwise detach from its row).
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    const onScrollOrResize = () => setMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [menuOpen]);

  const statusVariant =
    workflow.status === "active" ? "success" : workflow.status === "paused" ? "warning" : "secondary";
  return (
    <tr
      className="group cursor-pointer border-b border-border/50 transition-colors hover:bg-surface-hover"
      onClick={onOpen}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent">
            <WorkflowIcon size={15} />
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-text-primary">{workflow.name}</p>
            {workflow.description && (
              <p className="truncate text-xs text-text-muted">{workflow.description}</p>
            )}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Badge variant={statusVariant} className="capitalize">
          {workflow.status}
        </Badge>
      </td>
      <td className="truncate px-4 py-3 text-text-secondary">
        {workflow.agentCount} agent{workflow.agentCount === 1 ? "" : "s"}
      </td>
      <td className="truncate px-4 py-3 text-text-muted max-md:hidden">
        {workflow.lastRunAt ? formatRelative(workflow.lastRunAt) : "—"}
      </td>
      <td className="truncate px-4 py-3 text-text-muted max-md:hidden">{formatDate(workflow.createdAt)}</td>
      <td className="px-4 py-3">
        {isAdmin && (
          <div className="relative">
            <button
              ref={btnRef}
              aria-label="Row actions"
              onClick={(e) => {
                e.stopPropagation();
                if (menuOpen) setMenuOpen(false);
                else openMenu();
              }}
              className="flex h-7 w-7 items-center justify-center rounded text-text-muted opacity-0 transition-opacity hover:bg-black/10 group-hover:opacity-100 dark:hover:bg-white/10"
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen &&
              createPortal(
                <div
                  ref={popRef}
                  style={{ position: "fixed", top: menuPos.top, left: menuPos.left, width: MENU_W }}
                  className="z-50 overflow-hidden rounded-lg border border-border bg-background shadow-lg"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onDuplicate();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-text-primary transition-colors hover:bg-surface-hover"
                  >
                    <Copy size={12} />
                    Duplicate
                  </button>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      onDelete();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-500 transition-colors hover:bg-red-500/10"
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                </div>,
                document.body,
              )}
          </div>
        )}
      </td>
    </tr>
  );
}

function EmptyState({
  canCreate,
  onCreate,
}: {
  canCreate: boolean;
  onCreate: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface/50 py-16 text-center">
      <WorkflowIcon size={32} className="mb-3 text-text-muted" />
      <p className="text-sm font-medium text-text-primary">No workflows yet</p>
      <p className="mt-1 max-w-sm text-xs text-text-muted">
        Create one to automate multi-step agent pipelines — triggers, conditions, and
        agent handoffs on a visual canvas.
      </p>
      {canCreate && (
        <Button size="sm" onClick={onCreate} className="mt-4 gap-2">
          <Plus size={14} />
          New Workflow
        </Button>
      )}
    </div>
  );
}

function AgentEmptyState({ agentName }: { agentName: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-surface/50 py-16 text-center">
      <WorkflowIcon size={32} className="mb-3 text-text-muted" />
      <p className="text-sm font-medium text-text-primary">
        No workflows for {agentName}
      </p>
      <p className="mt-1 max-w-sm text-xs text-text-muted">
        {agentName} isn't part of any workflow yet. Add an Agent node for this
        agent on a workflow's canvas, or switch to "All workflows" above to see
        the rest of the tenant's workflows.
      </p>
    </div>
  );
}

function NewWorkflowModal({
  onCancel,
  onConfirm,
  pending,
}: {
  onCancel: () => void;
  onConfirm: (name: string) => void;
  pending: boolean;
}) {
  const [name, setName] = useState("");
  const trimmed = name.trim();
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onCancel}
    >
      <div
        className="w-[min(420px,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3 text-sm font-semibold text-text-primary">New workflow</h3>
        <label className="mb-1 block text-xs text-text-secondary">Name</label>
        <input
          autoFocus
          data-tour="workflow-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && trimmed && !pending) onConfirm(trimmed);
            if (e.key === "Escape") onCancel();
          }}
          placeholder="Lead Qualification Pipeline"
          className="mb-4 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary outline-none focus:border-accent"
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            size="sm"
            data-tour="workflow-create-confirm"
            disabled={!trimmed || pending}
            onClick={() => onConfirm(trimmed)}
          >
            {pending ? "Creating…" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays} days ago`;
  return formatDate(iso);
}
